import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc } from 'firebase/firestore';
import { storage, db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

export default function AddProduct() {
  const [productData, setProductData] = useState({
    title: '',
    description: '',
    basePrice: '',
    category: '',
    images: [],
    options: [],
    discount: {
      type: 'none', // 'none', 'percentage', 'fixed'
      value: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, step: '' });
  const [uploadDebug, setUploadDebug] = useState({
    currentName: '',
    currentBytes: 0,
    currentTotalBytes: 0,
    currentPct: 0,
    lastEventAt: 0
  });

  const withTimeout = (promise, ms, label) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${ms}ms: ${label}`)), ms)
      )
    ]);
  };

  // Dropzone for image uploads
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    onDrop: (acceptedFiles) => {
      setProductData(prev => ({
        ...prev,
        images: [...prev.images, ...acceptedFiles]
      }));
    }
  });

  // Handle option changes
  const handleOptionChange = (index, field, value) => {
    const newOptions = [...productData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setProductData(prev => ({ ...prev, options: newOptions }));
  };

  // Add new option
  const addOption = () => {
    setProductData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', choices: [], prices: {} }]
    }));
  };

  // Remove option
  const removeOption = (index) => {
    setProductData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Add choice to option
  const addChoice = (optionIndex) => {
    const newOptions = [...productData.options];
    newOptions[optionIndex].choices.push('');
    setProductData(prev => ({ ...prev, options: newOptions }));
  };

  // Remove choice from option
  const removeChoice = (optionIndex, choiceIndex) => {
    const newOptions = [...productData.options];
    newOptions[optionIndex].choices.splice(choiceIndex, 1);
    // Also remove corresponding price
    delete newOptions[optionIndex].prices[choiceIndex];
    setProductData(prev => ({ ...prev, options: newOptions }));
  };

  // Handle choice change
  const handleChoiceChange = (optionIndex, choiceIndex, value) => {
    const newOptions = [...productData.options];
    newOptions[optionIndex].choices[choiceIndex] = value;
    setProductData(prev => ({ ...prev, options: newOptions }));
  };

  // Handle price change for choice
  const handlePriceChange = (optionIndex, choiceIndex, value) => {
    const newOptions = [...productData.options];
    if (!newOptions[optionIndex].prices) {
      newOptions[optionIndex].prices = {};
    }
    newOptions[optionIndex].prices[choiceIndex] = parseFloat(value) || 0;
    setProductData(prev => ({ ...prev, options: newOptions }));
  };

  // Remove image
  const removeImage = (index) => {
    const imageToRemove = productData.images[index];
    
    // Clean up object URL if it's a local file
    if (imageToRemove && typeof imageToRemove !== 'string') {
      URL.revokeObjectURL(URL.createObjectURL(imageToRemove));
    }
    
    setProductData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Upload images to Firebase Storage
  const uploadImages = async () => {
    const total = productData.images.length;
    setUploadProgress({ current: 0, total, step: total ? 'starting' : 'no-images' });
    setUploadDebug({
      currentName: '',
      currentBytes: 0,
      currentTotalBytes: 0,
      currentPct: 0,
      lastEventAt: Date.now()
    });

    const uploadPromises = productData.images.map(async (image, idx) => {
      const safeName = (image?.name || `image_${idx}`).replace(/[^\w.-]+/g, '_');
      const path = `products/${uuidv4()}_${safeName}`;
      const imageRef = ref(storage, path);

      console.log('[AddProduct] uploading image', {
        idx: idx + 1,
        total,
        name: image?.name,
        type: image?.type,
        size: image?.size,
        path
      });

      setUploadProgress({ current: idx + 1, total, step: 'uploading' });

      // Use resumable uploads so we can see progress + detect stalls.
      const uploadTask = uploadBytesResumable(imageRef, image);
      const snapshot = await withTimeout(
        new Promise((resolve, reject) => {
          let lastBytes = 0;
          let lastTickAt = Date.now();
          const stallInterval = setInterval(() => {
            const now = Date.now();
            // If no progress events / byte movement for 25s, treat as stalled.
            if (now - lastTickAt > 25_000) {
              console.error('[AddProduct] upload appears stalled, cancelling task', {
                idx: idx + 1,
                total,
                path,
                name: image?.name,
                size: image?.size,
                lastBytes
              });
              try {
                uploadTask.cancel();
              } catch (_) {}
              clearInterval(stallInterval);
              reject(new Error(`Upload stalled for 25s: ${image?.name || path}`));
            }
          }, 1000);

          uploadTask.on(
            'state_changed',
            (snap) => {
              lastTickAt = Date.now();
              if (snap.bytesTransferred !== lastBytes) {
                lastBytes = snap.bytesTransferred;
              }
              const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;

              setUploadDebug({
                currentName: image?.name || safeName,
                currentBytes: snap.bytesTransferred,
                currentTotalBytes: snap.totalBytes,
                currentPct: pct,
                lastEventAt: Date.now()
              });

              // High-signal periodic logs (every ~10%)
              if (pct % 10 === 0) {
                console.log('[AddProduct] upload progress', {
                  idx: idx + 1,
                  total,
                  pct,
                  bytesTransferred: snap.bytesTransferred,
                  totalBytes: snap.totalBytes,
                  state: snap.state,
                  path
                });
              }
            },
            (err) => {
              clearInterval(stallInterval);
              reject(err);
            },
            () => {
              clearInterval(stallInterval);
              resolve(uploadTask.snapshot);
            }
          );
        }),
        120_000,
        `uploadBytesResumable(${path})`
      );

      console.log('[AddProduct] uploadBytes done', {
        idx: idx + 1,
        total,
        fullPath: snapshot?.ref?.fullPath
      });

      setUploadProgress({ current: idx + 1, total, step: 'gettingDownloadURL' });
      const url = await withTimeout(getDownloadURL(snapshot.ref), 30_000, `getDownloadURL(${path})`);

      console.log('[AddProduct] getDownloadURL done', { idx: idx + 1, total, url });
      return url;
    });

    const urls = await Promise.all(uploadPromises);
    setUploadProgress({ current: total, total, step: 'done' });
    return urls;
  };

  // Get preview URL for local images
  const getImagePreviewUrl = (image) => {
    if (typeof image === 'string') {
      return image; // Already a URL
    }
    return URL.createObjectURL(image);
  };

  // Create sample photobox product
  const createSamplePhotobox = () => {
    setProductData({
      title: 'Hand-Cranked Photo Album with Light',
      description: 'Beautiful wooden photo album with mechanical hand-crank mechanism. Features LED lighting and customizable photo options. Perfect gift for special occasions.',
      basePrice: '25.95',
      category: 'Photobox',
      images: [],
      options: [
        {
          name: 'How Many Pictures',
          choices: ['8', '16'],
          prices: { 0: 0, 1: 5.00 }
        },
        {
          name: 'Style',
          choices: ['With light & free charging cable', 'Without light'],
          prices: { 0: 3.00, 1: 0 }
        },
        {
          name: 'Music Playback',
          choices: ['Standard Version (No music playback)', 'Music Version (Supports music playback)'],
          prices: { 0: 0, 1: 8.00 }
        }
      ],
      discount: {
        type: 'percentage',
        value: '10'
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setUploadProgress({ current: 0, total: productData.images.length, step: 'validating' });

    try {
      console.log('[AddProduct] submit productData', {
        title: productData.title,
        basePrice: productData.basePrice,
        category: productData.category,
        imagesCount: productData.images?.length,
        optionsCount: productData.options?.length
      });

      // Upload images first
      const imageUrls = await uploadImages();
      console.log('[AddProduct] image upload complete', { imageUrlsCount: imageUrls.length });

      // Prepare product data for Firestore
      const productToSave = {
        ...productData,
        basePrice: parseFloat(productData.basePrice),
        images: imageUrls,
        createdAt: new Date(),
        updatedAt: new Date(),
        active: true
      };

      // Add to Firestore
      setUploadProgress((p) => ({ ...p, step: 'savingProduct' }));
      await addDoc(collection(db, 'products'), productToSave);
      console.log('[AddProduct] product saved to Firestore');

      setMessage('✅ Product added successfully!');
      
      // Reset form
      setProductData({
        title: '',
        description: '',
        basePrice: '',
        category: '',
        images: [],
        options: [],
        discount: {
          type: 'none',
          value: ''
        }
      });

    } catch (error) {
      console.error('Error adding product:', error);
      // Extra diagnostics for Firebase Storage errors like `storage/unknown`
      console.error('[AddProduct] error.code:', error?.code);
      console.error('[AddProduct] error.serverResponse:', error?.customData?.serverResponse);
      setMessage('❌ Error adding product: ' + (error?.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setUploadProgress((p) => ({ ...p, step: p.step === 'done' ? 'done' : 'idle' }));
    }
  };

  return (
    <div className="add-product-container">
      <h2>Add New Product</h2>
      
      {/* Sample Product Button */}
      <div className="sample-product-section">
        <button 
          type="button" 
          onClick={createSamplePhotobox}
          className="sample-btn"
        >
          🎯 Create Sample Photobox Product
        </button>
        <p className="sample-help">Click this button to pre-fill the form with a sample photobox product for testing.</p>
      </div>
      
      <form onSubmit={handleSubmit} className="product-form">
        {/* Basic Product Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="title">Product Title *</label>
            <input
              type="text"
              id="title"
              value={productData.title}
              onChange={(e) => setProductData(prev => ({ ...prev, title: e.target.value }))}
              required
              placeholder="Enter product title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={productData.description}
              onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))}
              required
              rows="4"
              placeholder="Enter product description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="basePrice">Base Price *</label>
              <input
                type="number"
                id="basePrice"
                value={productData.basePrice}
                onChange={(e) => setProductData(prev => ({ ...prev, basePrice: e.target.value }))}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                value={productData.category}
                onChange={(e) => setProductData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Photobox, Books, Electronics"
              />
            </div>
          </div>
        </div>

        {/* Product Options */}
        <div className="form-section">
          <h3>Product Options</h3>
          <p className="form-help">Add customizable options like pages, lights, etc.</p>
          
          {productData.options.map((option, optionIndex) => (
            <div key={optionIndex} className="option-container">
              <div className="option-header">
                <input
                  type="text"
                  value={option.name}
                  onChange={(e) => handleOptionChange(optionIndex, 'name', e.target.value)}
                  placeholder="Option name (e.g., Pages, Lights)"
                  className="option-name"
                />
                <button
                  type="button"
                  onClick={() => removeOption(optionIndex)}
                  className="remove-button"
                >
                  🗑️ Remove
                </button>
              </div>

              <div className="choices-container">
                {option.choices.map((choice, choiceIndex) => (
                  <div key={choiceIndex} className="choice-row">
                    <input
                      type="text"
                      value={choice}
                      onChange={(e) => handleChoiceChange(optionIndex, choiceIndex, e.target.value)}
                      placeholder="Choice value (e.g., 8 pages, Yes)"
                      className="choice-input"
                    />
                    <input
                      type="number"
                      value={option.prices?.[choiceIndex] || ''}
                      onChange={(e) => handlePriceChange(optionIndex, choiceIndex, e.target.value)}
                      placeholder="Extra cost"
                      min="0"
                      step="0.01"
                      className="price-input"
                    />
                    <button
                      type="button"
                      onClick={() => removeChoice(optionIndex, choiceIndex)}
                      className="remove-button small"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => addChoice(optionIndex)}
                  className="add-button"
                >
                  ➕ Add Choice
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addOption}
            className="add-button"
          >
            ➕ Add Option
          </button>
        </div>

        {/* Discount Settings */}
        <div className="form-section">
          <h3>Discount Settings</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="discountType">Discount Type</label>
              <select
                id="discountType"
                value={productData.discount.type}
                onChange={(e) => setProductData(prev => ({
                  ...prev,
                  discount: { ...prev.discount, type: e.target.value }
                }))}
              >
                <option value="none">No Discount</option>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>

            {productData.discount.type !== 'none' && (
              <div className="form-group">
                <label htmlFor="discountValue">Discount Value</label>
                <input
                  type="number"
                  id="discountValue"
                  value={productData.discount.value}
                  onChange={(e) => setProductData(prev => ({
                    ...prev,
                    discount: { ...prev.discount, value: e.target.value }
                  }))}
                  min="0"
                  step={productData.discount.type === 'percentage' ? '1' : '0.01'}
                  placeholder={productData.discount.type === 'percentage' ? '10' : '5.00'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Image Upload */}
        <div className="form-section">
          <h3>Product Images</h3>
          <p className="form-help">Upload product preview images</p>
          
          <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the images here...</p>
            ) : (
              <p>Drag & drop images here, or click to select files</p>
            )}
          </div>

          {productData.images.length > 0 && (
            <div className="image-preview">
              <h4>Selected Images ({productData.images.length})</h4>
              <div className="image-grid">
                {productData.images.map((image, index) => (
                  <div key={index} className="image-item">
                    <img
                      src={getImagePreviewUrl(image)}
                      alt={`Preview ${index + 1}`}
                      className="preview-image"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="remove-image-button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Adding Product...' : 'Add Product'}
          </button>
        </div>

        {loading && (
          <div className="message">
            Upload: {uploadProgress.current}/{uploadProgress.total} ({uploadProgress.step})
            {uploadDebug.currentName ? (
              <>
                <br />
                File: {uploadDebug.currentName} — {uploadDebug.currentPct}% ({uploadDebug.currentBytes}/{uploadDebug.currentTotalBytes} bytes)
              </>
            ) : null}
          </div>
        )}

        {/* Message Display */}
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
}
